import React, { ReactNode } from 'react'
import { useBadgeRotation } from '../../hooks/useBadgeRotation'
import '../../styles/TrustSignalsModule/MobileRotator.scss'

export interface MobileRotatorProps {
  badges: Array<{ key: string, node: ReactNode, staged?: boolean }>
}

/**
 * Renders every enabled badge once. CSS controls the layout:
 *  - desktop: horizontal flex row, all slots fully visible.
 *  - mobile: slots stack via absolute positioning. Only the slot marked
 *    `--active` is opacity 1; the rest are 0.
 *
 * A `staged` badge is mounted but invisible and out of rotation, so a third-party
 * widget can render before its slot is shown.
 *
 * Keeping a single mount per badge avoids re-injecting BBB / Trustpilot
 * embed scripts and duplicate `id="bbblink"` in the DOM. The rotator's JS
 * loop only matters on mobile, but it runs in both layouts because we
 * don't gate on viewport at the JS layer (no media-query hook in repo).
 *
 * `aria-hidden="true"` is intentionally NOT applied to the wrapper —
 * the desktop layout is the SR-visible content. On mobile, the rotating
 * cycle is decorative; the same DOM still carries badge alt text so SR
 * users get the badges read once on first render.
 */
export const TrustSignalsRotator = ({ badges }: MobileRotatorProps) => {
  const visibleCount = badges.filter(({ staged }) => staged !== true).length
  const { activeIndex, reducedMotion } = useBadgeRotation(visibleCount)

  if (badges.length === 0) return null

  const rotatorClassName = reducedMotion
    ? 'trust-signals__rotator trust-signals__rotator--reduced-motion'
    : 'trust-signals__rotator'

  let visibleIndex = -1
  return (
    <div className={rotatorClassName}>
      {badges.map(({ key, node, staged }) => {
        let slotClassName = 'trust-signals__slot trust-signals__slot--staging'
        if (staged !== true) {
          visibleIndex += 1
          slotClassName = visibleIndex === activeIndex
            ? 'trust-signals__slot trust-signals__slot--active'
            : 'trust-signals__slot'
        }
        return (
          <div key={key} className={slotClassName}>
            {node}
          </div>
        )
      })}
    </div>
  )
}
